import { useEffect, useMemo, useState } from "react";
import { Form, Col, Row, Button } from "antd";
import useGet from "../../../global/hooks/useGet";
import usePost from "../../../global/hooks/usePost";
import usePut from "../../../global/hooks/usePut";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import { CheckCircleOutlined, CloseOutlined } from "@ant-design/icons";
import CustomSelect from "../../../global/components/inputWidgets/CustomSelect";
import CustomInput from "../../../global/components/inputWidgets/CustomInput";
import { getModifiedResponse } from "../../../global/utils/responseModifier";

const GIFT_CARDS_BASE = `${process.env.REACT_APP_API_URL}/api/giftcards`;

/** Door + device SKUs: raw counts in the form match the API (1–255 or 1–65535). */
const NUMERIC_DEVICE_ITEM_IDS = [3, 202, 203, 204, 205, 206, 207, 208];
/** Mobile Credential: form uses UI scale (×100); API receives value / 100 (same as MySite / OptionCards). */
const MOBILE_CREDENTIAL_ITEM_ID = 209;
const PER_UNIT_LICENSE_ITEM_IDS = [
  ...NUMERIC_DEVICE_ITEM_IDS,
  MOBILE_CREDENTIAL_ITEM_ID,
];
/** Door (3) and Directory (208) allow up to 65535; others max 255. */
const DEVICE_CODE_HIGH_CAP_ITEMS = new Set([3, 208]);

const MOBILE_CREDENTIAL_UI_MIN = 100;
const MOBILE_CREDENTIAL_UI_MAX = 25500;
const MOBILE_CREDENTIAL_UI_STEP = 100;

function getDeviceCodeRange(itemId) {
  const n = Number(itemId);
  if (DEVICE_CODE_HIGH_CAP_ITEMS.has(n)) return { min: 1, max: 65535 };
  return { min: 1, max: 255 };
}

function deviceCodeBoundsMessage(min, max) {
  return `Please enter a number between ${min} and ${max}.`;
}

function buildMobileCredentialQuantityOptions() {
  const options = [];
  for (
    let v = MOBILE_CREDENTIAL_UI_MIN;
    v <= MOBILE_CREDENTIAL_UI_MAX;
    v += MOBILE_CREDENTIAL_UI_STEP
  ) {
    options.push({ label: String(v), value: v });
  }
  return options;
}

function mobileCredentialBoundsMessage() {
  return `Choose a value from ${MOBILE_CREDENTIAL_UI_MIN.toLocaleString()} to ${MOBILE_CREDENTIAL_UI_MAX.toLocaleString()} in steps of ${MOBILE_CREDENTIAL_UI_STEP}.`;
}

export const PrepurchasedLicenseItemForm = ({
  giftCardId,
  editingItem,
  editingItemNo,
  existingItems = [],
  onCancel,
  onSaved,
}) => {
  const toValidNumber = (value) => {
    if (value === null || value === undefined || value === "") return undefined;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  };

  const [selectedItem, setSelectedItem] = useState(null);
  const [form] = Form.useForm();
  const selectedOldCode = Form.useWatch("OldCode", form);

  const postUrl = giftCardId && `${GIFT_CARDS_BASE}/${giftCardId}/items/`;
  const resolvedEditItemNo = editingItemNo ?? editingItem?.ItemNo;
  const putItemUrl =
    giftCardId && resolvedEditItemNo != null
      ? `${GIFT_CARDS_BASE}/${giftCardId}/items/${resolvedEditItemNo}/`
      : null;
  const optionUrl = `${process.env.REACT_APP_API_URL}/api/licensecodes/getoptions/`;
  const isNumericDeviceItem =
    selectedItem != null &&
    NUMERIC_DEVICE_ITEM_IDS.includes(Number(selectedItem));
  const isMobileCredentialItem =
    Number(selectedItem) === MOBILE_CREDENTIAL_ITEM_ID;
  const isPerUnitLicenseItem =
    selectedItem != null &&
    PER_UNIT_LICENSE_ITEM_IDS.includes(Number(selectedItem));

  const licenseUrl =
    selectedItem != null && !isPerUnitLicenseItem
      ? `${process.env.REACT_APP_API_URL}/api/licensecodes/codes/?limit=100&sort_by=Item&order=asc&page=1&Item=${selectedItem}`
      : null;

  const {
    data: optionData,
    loading: optionDataLoading,
    refetch: optionDataRefetch,
  } = useGet(optionUrl);

  const { data: licenseOptionData, loading: licenseOptionDataLoading } =
    useGet(licenseUrl);

  const licenseOptions = licenseOptionData?.data
    ?.filter((item) => !(selectedItem === 1 && [3, 4].includes(item.Code)))
    .map((item) => ({
      label: item.CodeName,
      value: item.Code,
    }));

  const hideOldLicense =
    Boolean(selectedItem) &&
    !isPerUnitLicenseItem &&
    !licenseOptionDataLoading &&
    Array.isArray(licenseOptions) &&
    licenseOptions.length <= 1;

  const oldCodeNum = Number(selectedOldCode);
  /** New license must be strictly greater than old (upgrade only: Old Code < New Code). */
  const newLicenseOptions = hideOldLicense
    ? licenseOptions
    : licenseOptions?.filter((opt) => {
        const codeNum = Number(opt.value);
        if (
          selectedOldCode === undefined ||
          selectedOldCode === null ||
          selectedOldCode === ""
        ) {
          return true;
        }
        if (!Number.isFinite(oldCodeNum) || !Number.isFinite(codeNum)) return true;
        return codeNum > oldCodeNum;
      });

  const modifiedItems = getModifiedResponse(optionData, "items");

  const {
    data: postData,
    loading: postLoading,
    error: postError,
    postData: postAction,
  } = usePost(postUrl);

  const putHookUrl =
    putItemUrl ??
    (giftCardId ? `${GIFT_CARDS_BASE}/${giftCardId}/items/0/` : "");
  const {
    putData: putAction,
    loading: putLoading,
    error: putError,
    data: putData,
  } = usePut(putHookUrl);

  const isEdit = resolvedEditItemNo != null;
  const saving = postLoading || putLoading;
  const editingItemValue =
    toValidNumber(editingItem?.Item) ??
    toValidNumber(editingItem?.LicenseItem) ??
    modifiedItems?.find(
      (option) => String(option?.label) === String(editingItem?.LicenseItem),
    )?.value;
  const usedItemValues = new Set(
    (existingItems ?? [])
      .map((item) => {
        const numericValue =
          toValidNumber(item?.Item) ?? toValidNumber(item?.LicenseItem);
        if (numericValue != null) return numericValue;
        const matchedOption = (modifiedItems ?? []).find(
          (option) => String(option?.label) === String(item?.LicenseItem),
        );
        return matchedOption?.value;
      })
      .filter((value) => value != null),
  );
  const filteredItemOptions = (modifiedItems ?? []).filter((option) => {
    if (isEdit && option.value === editingItemValue) return true;
    return !usedItemValues.has(option.value);
  });

  const deviceItemLabel =
    modifiedItems?.find((o) => Number(o.value) === Number(selectedItem))
      ?.label ?? "Device";

  const deviceCodeRange = isNumericDeviceItem
    ? getDeviceCodeRange(selectedItem)
    : null;

  const mobileCredentialQuantityOptions = useMemo(
    () => buildMobileCredentialQuantityOptions(),
    [],
  );

  const mobileCredentialOldFormItemProps = useMemo(
    () => ({
      rules: [
        {
          validator: (_, v) => {
            if (v === undefined || v === null || v === "") {
              return Promise.reject(
                new Error(
                  `Old License is required`,
                ),
              );
            }
            const n = Number(v);
            if (
              !Number.isFinite(n) ||
              n < MOBILE_CREDENTIAL_UI_MIN ||
              n > MOBILE_CREDENTIAL_UI_MAX ||
              n % MOBILE_CREDENTIAL_UI_STEP !== 0
            ) {
              return Promise.reject(
                new Error(mobileCredentialBoundsMessage()),
              );
            }
            return Promise.resolve();
          },
        },
      ],
    }),
    [deviceItemLabel],
  );

  const mobileCredentialNewFormItemProps = useMemo(
    () => ({
      dependencies: ["OldCode"],
      rules: [
        {
          validator: (_, v) => {
            if (v === undefined || v === null || v === "") {
              return Promise.reject(
                new Error(
                  `New License is required`,
                ),
              );
            }
            const n = Number(v);
            if (
              !Number.isFinite(n) ||
              n < MOBILE_CREDENTIAL_UI_MIN ||
              n > MOBILE_CREDENTIAL_UI_MAX ||
              n % MOBILE_CREDENTIAL_UI_STEP !== 0
            ) {
              return Promise.reject(
                new Error(mobileCredentialBoundsMessage()),
              );
            }
            const oldV = form.getFieldValue("OldCode");
            if (oldV !== undefined && oldV !== null && oldV !== "") {
              const oldN = Number(oldV);
              if (Number.isFinite(oldN) && n <= oldN) {
                return Promise.reject(
                  new Error(
                    "The new license count must be greater than the old license count.",
                  ),
                );
              }
            }
            return Promise.resolve();
          },
        },
      ],
    }),
    [deviceItemLabel, form],
  );

  const deviceOldRules = useMemo(() => {
    if (!deviceCodeRange) return [];
    const { min, max } = deviceCodeRange;
    const boundsMsg = deviceCodeBoundsMessage(min, max);
    return [
      {
        validator: (_, v) => {
          if (v === undefined || v === null || v === "") {
            return Promise.reject(
              new Error(`Old License is required`),
            );
          }
          const n = Number(v);
          if (
            !Number.isFinite(n) ||
            !Number.isInteger(n) ||
            n < min ||
            n > max
          ) {
            return Promise.reject(new Error(boundsMsg));
          }
          return Promise.resolve();
        },
      },
    ];
  }, [deviceItemLabel, deviceCodeRange?.min, deviceCodeRange?.max]);

  const deviceNewRules = useMemo(() => {
    if (!deviceCodeRange) return [];
    const { min, max } = deviceCodeRange;
    const boundsMsg = deviceCodeBoundsMessage(min, max);
    return [
      {
        validator: (_, v) => {
          if (v === undefined || v === null || v === "") {
            return Promise.reject(
              new Error(`New License is required`),
            );
          }
          const n = Number(v);
          if (
            !Number.isFinite(n) ||
            !Number.isInteger(n) ||
            n < min ||
            n > max
          ) {
            return Promise.reject(new Error(boundsMsg));
          }
          const oldV = form.getFieldValue("OldCode");
          if (oldV !== undefined && oldV !== null && oldV !== "") {
            const oldN = Number(oldV);
            if (Number.isFinite(oldN) && n <= oldN) {
              return Promise.reject(
                new Error(
                  "The new license count must be greater than the old license count.",
                ),
              );
            }
          }
          return Promise.resolve();
        },
      },
    ];
  }, [deviceItemLabel, deviceCodeRange?.min, deviceCodeRange?.max, form]);

  const newLicenseSelectFormItemProps = useMemo(() => {
    if (hideOldLicense) {
      return {};
    }
    return {
      dependencies: ["OldCode"],
      rules: [
        {
          validator: (_, value) => {
            if (value === undefined || value === null || value === "") {
              return Promise.reject(new Error("New License is required"));
            }
            const oldV = form.getFieldValue("OldCode");
            if (oldV === undefined || oldV === null || oldV === "") {
              return Promise.resolve();
            }
            if (Number(value) <= Number(oldV)) {
              return Promise.reject(
                new Error(
                  "The new license must be greater than the old license.",
                ),
              );
            }
            return Promise.resolve();
          },
        },
      ],
    };
  }, [hideOldLicense, form]);

  const onFinish = async (values) => {
    const licenseItem =
      toValidNumber(values.Item) ??
      toValidNumber(editingItem?.Item) ??
      toValidNumber(editingItem?.LicenseItem);
    const optCount = licenseOptions?.length ?? 0;
    const perUnitMode =
      licenseItem != null &&
      PER_UNIT_LICENSE_ITEM_IDS.includes(Number(licenseItem));
    const mobileCredentialMode =
      Number(licenseItem) === MOBILE_CREDENTIAL_ITEM_ID;
    const singleOrNoLicenseOption =
      !perUnitMode &&
      Array.isArray(licenseOptions) &&
      optCount <= 1;
    let oldCode = singleOrNoLicenseOption
      ? null
      : (toValidNumber(values.OldCode) ?? null);
    let newCode = toValidNumber(values.NewCode);
    if (mobileCredentialMode) {
      if (oldCode != null) oldCode = oldCode / 100;
      if (newCode != null) newCode = newCode / 100;
    }
    const modifiedValue = {
      LicenseItem: licenseItem,
      OldCode: oldCode,
      NewCode: newCode ?? null,
    };
    try {
      if (isEdit) {
        await putAction(modifiedValue);
      } else {
        await postAction(modifiedValue);
      }
    } catch (err) {
      openErrorNotification("Something went wrong.");
      console.log(err);
    }
    form.resetFields();
  };

  useEffect(() => {
    optionDataRefetch();
  }, []);

  useEffect(() => {
    form.resetFields();
    if (editingItem) {
      setSelectedItem(editingItemValue);
      const editIsMobile209 =
        Number(editingItemValue) === MOBILE_CREDENTIAL_ITEM_ID;
      const rawOld = toValidNumber(editingItem?.OldCode);
      const rawNew = toValidNumber(editingItem?.NewCode);
      form.setFieldsValue({
        Item: editingItemValue,
        OldCode:
          editIsMobile209 && rawOld != null ? rawOld * 100 : rawOld,
        NewCode:
          editIsMobile209 && rawNew != null ? rawNew * 100 : rawNew,
      });
    } else {
      setSelectedItem(null);
    }
  }, [editingItem, editingItemValue, form]);

  useEffect(() => {
    if (hideOldLicense) {
      form.setFieldsValue({ OldCode: undefined });
    }
  }, [hideOldLicense, form]);

  const soleNewLicenseCode =
    hideOldLicense &&
    Array.isArray(licenseOptions) &&
    licenseOptions.length === 1
      ? licenseOptions[0]?.value
      : undefined;

  useEffect(() => {
    if (soleNewLicenseCode === undefined || soleNewLicenseCode === null) {
      return;
    }
    const current = form.getFieldValue("NewCode");
    if (current != null && current !== "") {
      return;
    }
    form.setFieldsValue({ NewCode: soleNewLicenseCode });
  }, [soleNewLicenseCode, form]);

  useEffect(() => {
    if (!isPerUnitLicenseItem) return;
    const newV = form.getFieldValue("NewCode");
    if (
      selectedOldCode === undefined ||
      selectedOldCode === null ||
      selectedOldCode === ""
    ) {
      return;
    }
    if (newV === undefined || newV === null || newV === "") return;
    const oldN = Number(selectedOldCode);
    const newN = Number(newV);
    if (!Number.isFinite(oldN) || !Number.isFinite(newN)) return;
    if (newN <= oldN) {
      form.setFieldsValue({ NewCode: undefined });
    }
  }, [selectedOldCode, isPerUnitLicenseItem, form]);

  useEffect(() => {
    if (postError) {
      openErrorNotification(
        postError?.response?.data?.message || "Something went wrong",
      );
    } else if (!postLoading && !postError && postData && !isEdit) {
      onSaved?.();
      onCancel();
      openConfirmNotification("Success");
    }
  }, [postError, postData, postLoading, isEdit, onCancel, onSaved]);

  useEffect(() => {
    if (putError) {
      openErrorNotification(
        putError?.response?.data?.message || "Something went wrong",
      );
    } else if (!putLoading && !putError && putData && isEdit) {
      onSaved?.();
      onCancel();
      openConfirmNotification("Success");
    }
  }, [putError, putData, putLoading, isEdit, onCancel, onSaved]);

  return (
    <>
      <Form
        validateTrigger={"onChange"}
        validateFirst
        form={form}
        onFinish={onFinish}
        scrollToFirstError
        layout={"vertical"}
        autoComplete={"off"}
      >
        <div className="px-2 pt-0 pb-3">
          <Row gutter={[16, 8]}>
            <Col xs={24} sm={24} md={24} lg={24} xl={24}>
              <CustomSelect
                label="License Item"
                name="Item"
                options={filteredItemOptions}
                placeholder="Please select"
                required
                loading={optionDataLoading}
                onChange={(value) => {
                  setSelectedItem(value);
                  form.setFieldsValue({
                    OldCode: undefined,
                    NewCode: undefined,
                  });
                }}
              />
            </Col>
            {isMobileCredentialItem ? (
              <>
                <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                  <CustomSelect
                    label={`Old License`}
                    name="OldCode"
                    options={mobileCredentialQuantityOptions}
                    placeholder="Please select"
                    required
                    disabled={!selectedItem}
                    formItemProps={mobileCredentialOldFormItemProps}
                    onChange={(value) => {
                      const currentNew = form.getFieldValue("NewCode");
                      if (
                        currentNew == null ||
                        currentNew === "" ||
                        value == null ||
                        value === ""
                      ) {
                        return;
                      }
                      const oldN = Number(value);
                      const newN = Number(currentNew);
                      if (!Number.isFinite(oldN) || !Number.isFinite(newN))
                        return;
                      if (newN <= oldN) {
                        form.setFieldsValue({ NewCode: undefined });
                      }
                    }}
                  />
                </Col>
                <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                  <CustomSelect
                    label={`New License`}
                    name="NewCode"
                    options={mobileCredentialQuantityOptions}
                    placeholder="Please select"
                    required
                    disabled={!selectedItem}
                    formItemProps={mobileCredentialNewFormItemProps}
                  />
                </Col>
              </>
            ) : isNumericDeviceItem && deviceCodeRange ? (
              <>
                <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                  <CustomInput
                    label={`Old License`}
                    name="OldCode"
                    type="number"
                    required
                    rules={deviceOldRules}
                    disable={!selectedItem}
                    min={deviceCodeRange.min}
                    max={deviceCodeRange.max}
                    step={1}
                  />
                </Col>
                <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                  <CustomInput
                    label={`New License`}
                    name="NewCode"
                    type="number"
                    required
                    rules={deviceNewRules}
                    dependencies={["OldCode"]}
                    disable={!selectedItem}
                    min={deviceCodeRange.min}
                    max={deviceCodeRange.max}
                    step={1}
                  />
                </Col>
              </>
            ) : (
              <>
                {!hideOldLicense ? (
                  <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <CustomSelect
                      label="Old License"
                      name="OldCode"
                      options={licenseOptions}
                      placeholder="Please select"
                      loading={licenseOptionDataLoading}
                      disabled={!selectedItem}
                      onChange={(value) => {
                        const currentNew = form.getFieldValue("NewCode");
                        if (
                          currentNew == null ||
                          currentNew === "" ||
                          value == null ||
                          value === ""
                        ) {
                          return;
                        }
                        const oldN = Number(value);
                        const newN = Number(currentNew);
                        if (!Number.isFinite(oldN) || !Number.isFinite(newN))
                          return;
                        if (newN <= oldN) {
                          form.setFieldsValue({ NewCode: undefined });
                        }
                      }}
                    />
                  </Col>
                ) : null}
                <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                  <CustomSelect
                    label="New License"
                    name="NewCode"
                    options={newLicenseOptions}
                    placeholder="Please select"
                    required
                    loading={licenseOptionDataLoading}
                    disabled={!selectedItem}
                    formItemProps={newLicenseSelectFormItemProps}
                  />
                </Col>
              </>
            )}
          </Row>
        </div>
      </Form>

      <div className="flex justify-center space-x-2 mt-2.5">
        <Button
          type="primary"
          className="mr-2 min-w-[104px] h-[30px] py-1 px-[10px] font-medium flex items-center "
          onClick={() => form.submit()}
          loading={saving}
          disabled={saving}
        >
          <CheckCircleOutlined className="!text-[14px] font-medium" />
          <span className="">Apply</span>
        </Button>
        <Button className="reset-button" onClick={onCancel}>
          <CloseOutlined />
          <span className="">Cancel</span>
        </Button>
      </div>
    </>
  );
};
